import { ReactNode, RefObject, useCallback, useEffect, useRef, useState } from "react";

import { Box } from "@mui/material";

type InfiniteScrollProps = {
  children: ReactNode;
  hasMore: boolean;
  isLoading?: boolean;
  onLoadMore: () => void | Promise<unknown>;
  loader?: ReactNode;
  rootMargin?: string;
  rootRef?: RefObject<HTMLElement | null>;
};

export const InfiniteScroll = ({
  children,
  hasMore,
  isLoading: externalIsLoading,
  onLoadMore,
  loader = null,
  rootMargin = "200px",
  rootRef,
}: InfiniteScrollProps) => {
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  const [internalIsLoading, setInternalIsLoading] = useState(false);

  const isExternallyControlled = externalIsLoading !== undefined;
  const isLoading = isExternallyControlled ? externalIsLoading : internalIsLoading;

  const onLoadMoreRef = useRef(onLoadMore);
  useEffect(() => {
    onLoadMoreRef.current = onLoadMore;
  }, [onLoadMore]);

  const triggerLoadMore = useCallback(async () => {
    if (isExternallyControlled) {
      onLoadMoreRef.current();
      return;
    }
    setInternalIsLoading(true);
    try {
      await onLoadMoreRef.current();
    } finally {
      setInternalIsLoading(false);
    }
  }, [isExternallyControlled]);

  useEffect(() => {
    const node = sentinelRef.current;
    if (!node || !hasMore || isLoading) {
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          void triggerLoadMore();
        }
      },
      { root: rootRef?.current ?? null, rootMargin }
    );
    observer.observe(node);

    return () => observer.disconnect();
  }, [hasMore, isLoading, rootMargin, rootRef, triggerLoadMore]);

  return (
    <>
      {children}
      {hasMore && isLoading ? loader : null}
      <Box ref={sentinelRef} aria-hidden />
    </>
  );
};
